// i18n
import 'src/locales/i18n';

// scrollbar
import 'simplebar-react/dist/simplebar.min.css';

// lightbox
import 'yet-another-react-lightbox/styles.css';
import 'yet-another-react-lightbox/plugins/captions.css';
import 'yet-another-react-lightbox/plugins/thumbnails.css';

// map
import 'mapbox-gl/dist/mapbox-gl.css';

// editor
import 'react-quill/dist/quill.snow.css';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

// image
import 'react-lazy-load-image-component/src/effects/blur.css';

// ----------------------------------------------------------------------


// routes
// theme
import ThemeProvider from 'src/theme';
// locales
import { LocalizationProvider } from 'src/locales';
// hooks
import { useScrollToTop } from 'src/hooks/use-scroll-to-top';
// components
import ProgressBar from 'src/components/progress-bar';
import { MotionLazy } from 'src/components/animate/motion-lazy';
import SnackbarProvider from 'src/components/snackbar/snackbar-provider';
import { SettingsProvider, SettingsDrawer, useSettingsContext } from 'src/components/settings';
import AuthGuard from './auth';
import DashboardLayout from './layouts/dashboard/layout';
import Router from './routes';
import WorkspaceGuard from './providers/workspace';
import { FiltersDrawer, FiltersProvider } from './components/filters';
import { useResponsive } from './hooks/use-responsive';
import { PermissionsProvider } from './utils/permissionsContext';

import { LicenseInfo } from '@mui/x-license';
import { useGetWorkspacesQuery } from './context/api/workspaces';
import { PreviousLocationProvider } from './components/previousLocationContext';
import { Auth0ProviderWithNavigate } from './components/auth0-provider-with-navigate';
import mixpanel from 'mixpanel-browser';
import { Suspense } from 'react';
import { SplashScreen } from './components/loading-screen';
import NavigationTracker from './analytics/navigationTracker';

LicenseInfo.setLicenseKey(process.env.MUI_KEY ?? "");

const MIXPANEL_TOKEN = process.env.MIXPANEL_PROJECT_TOKEN ?? "";

mixpanel.init(MIXPANEL_TOKEN, {
  debug: false,
});


export default function App() {
  useScrollToTop();
  const lgUp = useResponsive('up', 'lg');
  const { data: workspaces, ...workspaceQuery } = useGetWorkspacesQuery(null);

    const settings = useSettingsContext();
  
  return (
    <LocalizationProvider>
      <PermissionsProvider>
        <Auth0ProviderWithNavigate>
          <SettingsProvider
            defaultSettings={{
              themeMode: 'light',
              themeDirection: 'ltr',
              themeContrast: 'bold',
              themeLayout: 'vertical',
              themeColorPresets: 'default',
              themeStretch: false,
            }}
          >
            <FiltersProvider
              defaultFilters={{}}
            >
              <ThemeProvider>
                <MotionLazy>
                  <SnackbarProvider>
                    {!lgUp &&
                      <FiltersDrawer />
                    }
                    <SettingsDrawer />
                    <ProgressBar />
                    <Suspense fallback={<SplashScreen />}>
                      <AuthGuard>
                        <WorkspaceGuard>
                          <DashboardLayout>
                            <PreviousLocationProvider>
                              <NavigationTracker />
                              <Router />
                            </PreviousLocationProvider>
                          </DashboardLayout>
                        </WorkspaceGuard>
                      </AuthGuard>
                    </Suspense>
                  </SnackbarProvider>
                </MotionLazy>
              </ThemeProvider>
            </FiltersProvider>
          </SettingsProvider>
        </Auth0ProviderWithNavigate>
      </PermissionsProvider>
    </LocalizationProvider>
  );
}
